import React, { createContext, useContext, useState, useCallback } from 'react'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '../types/analytics'

// Environment configuration for analytics
const ENV = import.meta.env.VITE_ENV
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const isAnalyticsEnabled = ENV === 'production' && !!POSTHOG_KEY

export interface DrawerNavigationState {
  // Current drawer type and its associated data
  current: {
    type: 'post-insights' | 'brand-details' | 'newsletter-post-insights' | null
    data: any
  }
  // Navigation history stack
  history: Array<{
    type: 'post-insights' | 'brand-details' | 'newsletter-post-insights'
    data: any
  }>
  // Remember which tab was active when navigating away
  tabMemory: {
    [key: string]: string
  }
}

interface DrawerNavigationContextValue {
  state: DrawerNavigationState
  // Navigation methods
  openPostInsights: (post: any, fromBrandDetails?: boolean) => void
  openBrandDetails: (brand: any, fromPostInsights?: boolean) => void
  openNewsletterPostInsights: (post: any) => void
  goBack: () => void
  closeAll: () => void
  // Tab memory
  setTabMemory: (drawerId: string, tabId: string) => void
  getTabMemory: (drawerId: string) => string | undefined
  // History access
  canGoBack: () => boolean
  getBreadcrumbs: () => Array<{ label: string; onClick?: () => void }>
}

const DrawerNavigationContext = createContext<
  DrawerNavigationContextValue | undefined
>(undefined)

export const DrawerNavigationProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const posthog = usePostHog()
  const [state, setState] = useState<DrawerNavigationState>({
    current: { type: null, data: null },
    history: [],
    tabMemory: {}
  })

  // Helper to track events
  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      if (!isAnalyticsEnabled || !posthog) return
      posthog.capture(eventName, properties)
    },
    [posthog]
  )

  const openPostInsights = useCallback(
    (post: any, fromBrandDetails = false) => {
      // Track drawer opened
      trackEvent(AnalyticsEvents.POST_INSIGHTS_VIEWED, {
        postId: post?.id
      })
      trackEvent(AnalyticsEvents.DRAWER_OPENED, {
        drawerType: 'post_insights'
      })

      setState((prev) => {
        const newHistory =
          fromBrandDetails && prev.current.type
            ? [
                ...prev.history,
                { type: prev.current.type, data: prev.current.data }
              ]
            : prev.history

        return {
          ...prev,
          current: { type: 'post-insights', data: post },
          history: newHistory
        }
      })
    },
    [trackEvent]
  )

  const openBrandDetails = useCallback(
    (brand: any, fromPostInsights = false) => {
      // Track drawer opened
      trackEvent(AnalyticsEvents.BRAND_DETAILS_VIEWED, {
        brandId: brand?.id,
        brandName: brand?.brandName
      })
      trackEvent(AnalyticsEvents.DRAWER_OPENED, {
        drawerType: 'brand_details'
      })

      setState((prev) => {
        const newHistory =
          fromPostInsights && prev.current.type
            ? [
                ...prev.history,
                { type: prev.current.type, data: prev.current.data }
              ]
            : prev.history

        return {
          ...prev,
          current: { type: 'brand-details' as const, data: brand },
          history: newHistory
        }
      })
    },
    [trackEvent]
  )

  const openNewsletterPostInsights = useCallback((post: any) => {
    setState((prev) => ({
      ...prev,
      current: { type: 'newsletter-post-insights', data: post },
      history: []
    }))
  }, [])

  const goBack = useCallback(() => {
    setState((prev) => {
      if (prev.history.length === 0) {
        return { ...prev, current: { type: null, data: null } }
      }

      const newHistory = [...prev.history]
      const previousDrawer = newHistory.pop()!

      return {
        ...prev,
        current: previousDrawer,
        history: newHistory
      }
    })
  }, [])

  const closeAll = useCallback(() => {
    // Track drawer closed
    trackEvent(AnalyticsEvents.DRAWER_CLOSED, {
      drawerType: 'all'
    })

    setState({
      current: { type: null, data: null },
      history: [],
      tabMemory: {}
    })
  }, [trackEvent])

  const setTabMemory = useCallback((drawerId: string, tabId: string) => {
    setState((prev) => ({
      ...prev,
      tabMemory: { ...prev.tabMemory, [drawerId]: tabId }
    }))
  }, [])

  const getTabMemory = useCallback(
    (drawerId: string) => {
      return state.tabMemory[drawerId]
    },
    [state.tabMemory]
  )

  const canGoBack = useCallback(() => {
    return state.history.length > 0
  }, [state.history])

  const getBreadcrumbs = useCallback(() => {
    const breadcrumbs: Array<{ label: string; onClick?: () => void }> = []

    // Add history items
    state.history.forEach((item, index) => {
      if (item.type === 'post-insights') {
        breadcrumbs.push({
          label: `Post: ${item.data.caption?.substring(0, 30)}...`,
          onClick: () => {
            // Navigate back to this specific item
            setState((prev) => ({
              ...prev,
              current: item,
              history: prev.history.slice(0, index)
            }))
          }
        })
      } else if (item.type === 'brand-details') {
        breadcrumbs.push({
          label: `Brand: ${item.data.brandName}`,
          onClick: () => {
            setState((prev) => ({
              ...prev,
              current: item,
              history: prev.history.slice(0, index)
            }))
          }
        })
      }
    })

    // Add current item (without onClick since we're already there)
    if (state.current.type === 'post-insights') {
      breadcrumbs.push({
        label: `Post: ${state.current.data.caption?.substring(0, 30)}...`
      })
    } else if (state.current.type === 'brand-details') {
      breadcrumbs.push({
        label: `Brand: ${state.current.data.brandName}`
      })
    } else if (state.current.type === 'newsletter-post-insights') {
      breadcrumbs.push({
        label: `Newsletter Post: ${state.current.data.title?.substring(
          0,
          30
        )}...`
      })
    }

    return breadcrumbs
  }, [state])

  const value: DrawerNavigationContextValue = {
    state,
    openPostInsights,
    openBrandDetails,
    openNewsletterPostInsights,
    goBack,
    closeAll,
    setTabMemory,
    getTabMemory,
    canGoBack,
    getBreadcrumbs
  }

  return (
    <DrawerNavigationContext.Provider value={value}>
      {children}
    </DrawerNavigationContext.Provider>
  )
}

export const useDrawerNavigation = () => {
  const context = useContext(DrawerNavigationContext)
  if (!context) {
    throw new Error(
      'useDrawerNavigation must be used within a DrawerNavigationProvider'
    )
  }
  return context
}
